import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "../ui/button";
import { Link } from "react-router-dom";
import { queryClient } from "../../lib/queryClient";
import { formatDistanceToNow } from "date-fns";
import { getAlerts, acknowledgeAlert } from "../../api/axios";
import { AlertCircle, AlertTriangle, Info } from "lucide-react";

type BackendAlert = {
  _id: string;
  deviceId: string;
  severity: "critical" | "warning" | "info";
  message: string;
  details?: string;
  timestamp: string;
  acknowledged: boolean;
  resolved: boolean;
};

export default function AlertsList() {
  const { data: alerts = [], isLoading } = useQuery<BackendAlert[]>({
    queryKey: ["/api/alerts/recent"],
    queryFn: () => getAlerts({ resolved: false }),
    select: (data) => data.slice(0, 4),
  });

  const acknowledgeMutation = useMutation({
    mutationFn: (alertId: string) => acknowledgeAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/alerts/recent"] });
    },
  });

  if (isLoading) {
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg leading-6 font-medium text-neutral-dark">
            Recent Alerts
          </h2>
          <Link to="/alerts" className="text-sm font-medium text-primary hover:text-primary-dark">
            View all alerts
          </Link>
        </div>
        <div className="bg-white shadow rounded-lg border border-gray-200 p-6">
          <div className="animate-pulse space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex justify-between">
                <div className="flex space-x-4">
                  <div className="rounded-full bg-gray-200 h-10 w-10"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-20"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!alerts || alerts.length === 0) {
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg leading-6 font-medium text-neutral-dark">
            Recent Alerts
          </h2>
          <Link to="/alerts" className="text-sm font-medium text-primary hover:text-primary-dark">
            View all alerts
          </Link>
        </div>
        <div className="bg-white shadow rounded-lg border border-gray-200 p-6 text-center text-gray-500">
          No recent alerts.
        </div>
      </div>
    );
  }

  const alertsList = alerts;

  const handleAcknowledge = (alertId: string) => {
    acknowledgeMutation.mutate(alertId);
  };

  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case "critical":
        return {
          bgClass: "bg-danger bg-opacity-10",
          textClass: "text-danger",
          iconClass: "text-danger",
          icon: <AlertCircle className="h-5 w-5" />,
          badgeClass: "bg-danger bg-opacity-10 text-danger",
        };
      case "warning":
        return {
          bgClass: "bg-warning bg-opacity-10",
          textClass: "text-warning",
          iconClass: "text-warning",
          icon: <AlertTriangle className="h-5 w-5" />,
          badgeClass: "bg-warning bg-opacity-10 text-warning",
        };
      case "info":
        return {
          bgClass: "bg-blue-100",
          textClass: "text-blue-800",
          iconClass: "text-blue-500",
          icon: <Info className="h-5 w-5" />,
          badgeClass: "bg-blue-100 text-blue-800",
        };
      default:
        return {
          bgClass: "bg-gray-100",
          textClass: "text-gray-800",
          iconClass: "text-gray-500",
          icon: <Info className="h-5 w-5" />,
          badgeClass: "bg-gray-100 text-gray-800",
        };
    }
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg leading-6 font-medium text-neutral-dark">
          Recent Alerts
        </h2>
        <Link to="/alerts" className="text-sm font-medium text-primary hover:text-primary-dark">
          View all alerts
        </Link>
      </div>
      <div className="bg-white shadow overflow-hidden rounded-lg border border-gray-200">
        <ul className="divide-y divide-gray-200">
          {alertsList.map((alert) => {
            const styles = getSeverityStyles(alert.severity);
            return (
              <li key={alert._id}>
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div
                        className={`rounded-full w-8 h-8 flex items-center justify-center ${styles.bgClass}`}
                      >
                        <span className={styles.iconClass}>
                          {styles.icon}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-neutral-dark">
                          {alert.message}
                        </div>
                        <div className="text-sm text-gray-500">
                          {alert.details}
                        </div>
                      </div>
                    </div>
                    <div>
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${styles.badgeClass}`}
                      >
                        {alert.severity.charAt(0).toUpperCase() +
                          alert.severity.slice(1)}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 flex justify-between">
                    <div className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(alert.timestamp), {
                        addSuffix: true,
                      })}
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        className="text-xs px-2 py-1 h-auto"
                        size="sm"
                        onClick={() => handleAcknowledge(alert._id)}
                        disabled={acknowledgeMutation.isPending}
                      >
                        Acknowledge
                      </Button>
                      <Button
                        className="text-xs px-2 py-1 h-auto"
                        variant="outline"
                        size="sm"
                        asChild
                      >
                        <Link to={`/alerts/${alert._id}`}>View Details</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
